import React, { useEffect, useState } from 'react';
import { Layout, Menu, Typography, Button, Divider, Badge } from 'antd';
import {
  DashboardOutlined,
  BankOutlined,
  MedicineBoxOutlined,
  FileTextOutlined,
  LogoutOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';

const { Sider } = Layout;

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  hasPendingInventoryRequests?: boolean;
  hasLowStockAlerts?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ 
  open, 
  onClose, 
  hasPendingInventoryRequests = false, 
  hasLowStockAlerts = false 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);

  // Check if device is mobile
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Check if inventory requests tab should be highlighted
  const shouldHighlightInventoryRequests = hasPendingInventoryRequests || hasLowStockAlerts;

  const menuItems = [
    { key: '/main', icon: <DashboardOutlined />, label: 'Dashboard' },
    { key: '/main/branches', icon: <BankOutlined />, label: 'Branches' },
    { key: '/main/medicines', icon: <MedicineBoxOutlined />, label: 'Medicines' },
    { 
      key: '/main/inventory-requests', 
      icon: shouldHighlightInventoryRequests ? (
        <Badge dot color="#ff4d4f">
          <FileTextOutlined style={{ color: shouldHighlightInventoryRequests ? '#ff4d4f' : undefined }} />
        </Badge>
      ) : <FileTextOutlined />, 
      label: 'Inventory Requests' 
    },
    { key: '/main/profile', icon: <UserOutlined />, label: 'Profile' },
    // { key: '/main/sales', icon: <ShoppingCartOutlined />, label: 'Sales' },
    // { key: '/main/analytics', icon: <BarChartOutlined />, label: 'Analytics' },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    console.log('Menu clicked:', key, 'isMobile:', isMobile);
    navigate(key);
    // Only close sidebar on mobile devices after navigation
    // On desktop, keep sidebar open for better UX
    if (isMobile) {
      console.log('Closing sidebar on mobile after navigation');
      onClose();
    } else {
      console.log('Keeping sidebar open on desktop');
    }
  };

  return (
    <>
      {/* Overlay for mobile - click outside to close */}
      {isMobile && open && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 998,
          }}
          onClick={onClose}
        />
      )}
      
      <Sider
        width={200}
        style={{
          position: 'fixed',
          left: 0,
          top: 64,
          bottom: 0,
          zIndex: 999,
          background: '#fff',
          boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
          transform: open ? 'translateX(0)' : 'translateX(-200px)',
          transition: 'transform 0.3s ease',
        }}
      >
        <div style={{ padding: '16px' }}>
          <Typography.Title level={5} style={{ margin: 0, color: '#1890ff' }}>
            Menu
          </Typography.Title>
        </div>
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
          style={{ border: 'none' }}
        />
        
        
        {/* Action Buttons at Bottom */}
        <div style={{ 
          position: 'absolute', 
          bottom: 0, 
          left: 0, 
          right: 0, 
          padding: '16px',
          background: '#fff',
          borderTop: '1px solid #f0f0f0'
        }}>
          <Divider style={{ margin: '0 0 16px 0' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Button
              icon={<LogoutOutlined />}
              onClick={() => {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                window.location.href = '/login';
              }}
              block
            >
              Logout
            </Button>
          </div>
        </div>
      </Sider>
    </>
  );
};

export default Sidebar;
